import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isCheckoutInProgress } from '@/stores/checkout-activity.store';
import { SESSION_TICK_MS, WARNING_LEAD_MS } from './SessionTimeout.types';
import type {
  ExpiryReason,
  SessionTimeoutHookProps,
  SessionTimeoutState,
} from './SessionTimeout.types';
import {
  clearSessionRecords,
  ensureSessionStart,
  writeSignOutReason,
} from './sessionTimeoutStorage';
import { useActivityTracker } from './useActivityTracker';
import { useSessionTimeoutConfig } from './useSessionTimeoutConfig';

/**
 * Warns, then signs the customer out, once the company's configured idle or absolute timeout
 * elapses.
 *
 * ## Invariants:
 * - The absolute deadline is measured from session start and is NEVER extended by activity.
 *   Why: it is a cap on total session length, so a customer who keeps clicking is exactly who
 *   it exists to sign out.
 * - Expiry is decided by comparing wall-clock timestamps, never by counting ticks. Why:
 *   intervals do not fire while a device sleeps. A phone pocketed for an hour must expire on
 *   the first tick after waking, not an hour later.
 * - The sign-out path runs at most once, guarded by a ref rather than by state. Why: state
 *   updates are not flushed synchronously, so two ticks arriving before a re-render would both
 *   pass a state check and sign out twice.
 * - A booking in progress defers the IDLE deadline only (PB-5476, AC 6); the absolute cap still
 *   fires. Why: AC 8 requires the cap to sign out a customer who has been active throughout,
 *   and a cap any page could suspend would not be a cap. Known consequence: a tab abandoned on
 *   a checkout screen defers idle sign-out until the cap, so a company enabling this feature
 *   should configure a non-zero absolute timeout.
 * - Client-side timing is a convenience, not a security boundary: the server still owns token
 *   expiry. Nothing here should be relied on to end a session.
 */

const secondsUntil = (deadline: number, now: number): number =>
  Math.max(0, Math.ceil((deadline - now) / 1000));

export const useSessionTimeout = (props: SessionTimeoutHookProps): SessionTimeoutState => {
  const { companyId, userKey, signOut } = props;
  const { enabled, idleMs, absoluteMs } = useSessionTimeoutConfig(companyId);

  const [phase, setPhase] = useState<'active' | 'warning'>('active');
  const [reason, setReason] = useState<ExpiryReason | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const expiredRef = useRef(false);
  // Assigned in an effect, never during render (`react-hooks/refs`).
  const signOutRef = useRef(signOut);
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  const expire = useCallback((expiryReason: ExpiryReason) => {
    if (expiredRef.current) return;
    expiredRef.current = true;
    // Written before signing out so every other tab reacts, and so the sign-in screen can
    // explain what happened once the redirect lands.
    writeSignOutReason(expiryReason);
    void signOutRef.current();
  }, []);

  const { getLastActivity, markActive } = useActivityTracker(enabled, expire);

  // Warning lead is clamped so a very short configured timeout still gets a warning rather
  // than jumping straight to sign-out.
  const warningLeadMs = useMemo(
    () => (idleMs > 0 ? Math.min(WARNING_LEAD_MS, idleMs / 2) : WARNING_LEAD_MS),
    [idleMs]
  );

  // Stamped in an effect rather than a memo: `ensureSessionStart` reads storage and the clock,
  // which is not something a render may do. The tick reads the ref, so it needs no re-run.
  const sessionStartRef = useRef(0);
  useEffect(() => {
    sessionStartRef.current = enabled ? ensureSessionStart(userKey, Date.now()) : 0;
  }, [enabled, userKey]);

  // Tracks the checkout hold across ticks so the release edge can be detected.
  const wasHeldRef = useRef(false);

  useEffect(() => {
    // No state reset here: `phase` and `reason` are derived against `enabled` at return, so a
    // disabled feature reports "active" without a cascading render.
    if (!enabled) return undefined;

    const tick = () => {
      const now = Date.now();
      const lastActivity = getLastActivity();

      // A clock moved backwards (NTP correction, manual change) would otherwise read as an
      // enormous idle gap. Re-stamp instead of expiring.
      if (lastActivity > now) {
        markActive({ immediate: true });
        return;
      }

      const held = isCheckoutInProgress();
      if (wasHeldRef.current && !held) {
        // Checkout just finished. Re-stamp so the customer gets a full fresh idle window
        // rather than being expired on this very tick by a timestamp from before checkout.
        wasHeldRef.current = false;
        markActive({ immediate: true });
        return;
      }
      wasHeldRef.current = held;

      const idleDeadline = !held && idleMs > 0 ? lastActivity + idleMs : Infinity;
      const absoluteDeadline = absoluteMs > 0 ? sessionStartRef.current + absoluteMs : Infinity;
      const deadline = Math.min(idleDeadline, absoluteDeadline);
      if (deadline === Infinity) {
        setPhase('active');
        setReason(null);
        return;
      }

      const nextReason: ExpiryReason = absoluteDeadline <= idleDeadline ? 'absolute' : 'idle';

      if (now >= deadline) {
        expire(nextReason);
        return;
      }

      const lead = nextReason === 'idle' ? warningLeadMs : WARNING_LEAD_MS;
      if (now >= deadline - lead) {
        setPhase('warning');
        setReason(nextReason);
        setSecondsRemaining(secondsUntil(deadline, now));
      } else {
        setPhase('active');
        setReason(null);
      }
    };

    tick();
    const intervalId = setInterval(tick, SESSION_TICK_MS);
    return () => clearInterval(intervalId);
  }, [enabled, idleMs, absoluteMs, warningLeadMs, getLastActivity, markActive, expire]);

  // Clears the activity clock and absolute window for the next user of this browser;
  // `useAuthStore.logout()` has no hook of its own to do it.
  useEffect(() => () => clearSessionRecords(), []);

  const onStaySignedIn = useCallback(() => {
    // Unthrottled so other tabs see the extension immediately.
    markActive({ immediate: true });
    // The absolute cap is not extendable, so presence must not dismiss its warning — the modal
    // would only reappear on the next tick, having implied the deadline moved. The UI hides
    // this action for the cap; this keeps the hook honest too.
    if (reason === 'absolute') return;
    setPhase('active');
    setReason(null);
  }, [markActive, reason]);

  const onSignOutNow = useCallback(() => expire(reason ?? 'idle'), [expire, reason]);

  // Derived rather than reset in an effect: with the feature off there is nothing to report,
  // and leaving the last warning in state would resurface it if a company re-enabled mid-session.
  const effectivePhase = enabled ? phase : 'active';
  const effectiveReason = enabled ? reason : null;

  return {
    phase: effectivePhase,
    reason: effectiveReason,
    secondsRemaining,
    canExtend: effectiveReason === 'idle',
    onStaySignedIn,
    onSignOutNow,
  };
};
